import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { z } from "zod";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Radar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

/** The next roadmap milestone this page counts down to. */
const MILESTONE = {
  title: "Deeper validation",
  when: "October 2026",
  targetIso: "2026-10-01T09:00:00Z",
  items: [
    "Competitor snapshots on every blueprint",
    "Supplier and template shortlists per trend",
    "Saved niches with acceleration alerts",
  ],
};

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const emailSchema = z
  .string()
  .trim()
  .min(3, "Add your email to join the early-bird list.")
  .max(254, "Email is too long.")
  .regex(EMAIL_REGEX, "That doesn't look like a valid email.");

type Remaining = { days: number; hours: number; minutes: number; seconds: number; done: boolean };

const remainingFrom = (target: number): Remaining => {
  const diff = target - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  const seconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
    done: false,
  };
};

const pad = (n: number) => String(n).padStart(2, "0");

const Launch = () => {
  const target = useMemo(() => new Date(MILESTONE.targetIso).getTime(), []);
  const [left, setLeft] = useState<Remaining>(() => remainingFrom(target));
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setLeft(remainingFrom(target)), 1000);
    return () => window.clearInterval(id);
  }, [target]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    setStatus("loading");
    const { data, error: fnError } = await supabase.functions.invoke("early-bird-signup", {
      body: { email: parsed.data.toLowerCase(), source: "launch_early_bird" },
    });
    if (fnError || (data as { error?: string } | null)?.error) {
      setStatus("idle");
      const code = (data as { error?: string } | null)?.error;
      setError(
        code === "invalid_email"
          ? "That doesn't look like a valid email."
          : "Something glitched on our end. Try again in a moment.",
      );
      return;
    }
    setStatus("success");
  };


  const units: [string, number][] = [
    ["DAYS", left.days],
    ["HOURS", left.hours],
    ["MINUTES", left.minutes],
    ["SECONDS", left.seconds],
  ];

  return (
    <>
      <Helmet>
        <title>{`${MILESTONE.title} — ${MILESTONE.when} Launch | Trends Discovery`}</title>
        <meta
          name="description"
          content={`Trends Discovery ships ${MILESTONE.title} in ${MILESTONE.when}: competitor snapshots, supplier shortlists and acceleration alerts. Join the early-bird list.`}
        />
        <link rel="canonical" href="https://trendsdiscovery.com/launch" />
        <meta property="og:title" content={`${MILESTONE.title} — ${MILESTONE.when} launch`} />
        <meta
          property="og:description"
          content="Countdown and early-bird sign-up for the next Trends Discovery milestone."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://trendsdiscovery.com/launch" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <div className="container max-w-3xl py-16">
          <Link
            to="/roadmap"
            className="inline-flex items-center gap-2 font-mono text-xs text-primary hover:underline"
          >
            <ArrowLeft className="w-3 h-3" /> BACK TO ROADMAP
          </Link>

          <div className="mt-8 flex items-center gap-2">
            <span className="grid place-items-center w-7 h-7 rounded bg-primary text-primary-foreground">
              <Radar className="w-4 h-4" />
            </span>
            <span className="font-display font-semibold">Trends.Discovery</span>
          </div>

          <p className="mt-8 font-mono text-xs tracking-[0.2em] text-primary">
            NEXT MILESTONE · {MILESTONE.when.toUpperCase()}
          </p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-bold">{MILESTONE.title}</h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            The next step of the 2026 roadmap goes live for every licence holder. No extra cost —
            it is part of the one-time $39 lifetime licence.
          </p>

          <div className="mt-10 grid grid-cols-4 gap-3" aria-live="polite">
            {units.map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-border bg-card px-2 py-5 text-center"
              >
                <div className="font-display text-3xl md:text-4xl font-bold tabular-nums">
                  {pad(value)}
                </div>
                <div className="mt-1 font-mono text-[10px] tracking-widest text-muted-foreground">
                  {label}
                </div>
              </div>
            ))}
          </div>
          {left.done && (
            <p className="mt-4 font-mono text-xs text-primary">
              IT'S LIVE — OPEN YOUR DASHBOARD TO SEE IT.
            </p>
          )}

          <section className="mt-12 rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-xl font-bold">What ships</h2>
            <ul className="mt-4 space-y-3">
              {MILESTONE.items.map((i) => (
                <li key={i} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-8 rounded-2xl border border-primary/30 bg-primary/5 p-6">
            <h2 className="font-display text-xl font-bold">Early-bird list</h2>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Get an email the moment it goes live, plus first access while it is still in testing.
            </p>
            {status === "success" ? (
              <p className="mt-5 inline-flex items-center gap-2 text-sm text-primary">
                <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> You're on the list — watch
                your inbox.
              </p>
            ) : (
              <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3">
                <label htmlFor="early-bird-email" className="sr-only">
                  Email address
                </label>
                <Input
                  id="early-bird-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!error}
                  aria-describedby={error ? "early-bird-error" : undefined}
                  className="sm:flex-1"
                />
                <Button type="submit" disabled={status === "loading"}>
                  {status === "loading" ? (
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <>
                      Join the list <ArrowRight className="w-4 h-4" aria-hidden="true" />
                    </>
                  )}
                </Button>
              </form>
            )}
            {error && (
              <p id="early-bird-error" className="mt-3 text-sm text-destructive">
                {error}
              </p>
            )}
          </section>

          <div className="mt-10 flex flex-wrap gap-4 text-sm">
            <Link to="/launch-list" className="text-primary hover:underline">
              Early-bird launch list
            </Link>
            <Link to="/milestones" className="text-primary hover:underline">
              Live milestone progress

            </Link>
            <Link to="/marketplace" className="text-primary hover:underline">
              Browse the marketplace
            </Link>
            <Link to="/#pricing" className="text-primary hover:underline">
              Get the $39 lifetime licence
            </Link>
          </div>
        </div>
      </main>
    </>
  );
};

export default Launch;
